// Modale "Itinéraire" pour la fiche contact :
// - Champ "Départ" avec autocomplete HERE + bouton "Ma position" (geoloc native)
// - Champ "Arrivée" pré-rempli avec l'adresse du contact (modifiable)
// - Étapes intermédiaires (5 max), options façon Mappy : le plus rapide / le
//   plus court, éviter péages / autoroutes / ferries, heure de départ
// - Au calcul : jusqu'à 3 itinéraires comparés (durée, péages,
//   frais de déplacement), carte Leaflet + sidebar distance/durée/péages/étapes
// - Frais de déplacement : forfait de l'aller-retour (moitié par trajet) + km au-delà, majorés
//   nuit / dimanche / férié, d'après la grille de l'antenne (lib/fraisDeplacement)

import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { X, MapPin, Navigation, Loader2, Search, Crosshair, Route, Plus, Trash2, Clock, ExternalLink } from 'lucide-react';
import { autocomplete, geocode, reverseGeocode, route as routeHere, decodeFlexPolyline } from '../lib/hereApi';
import { createPortal } from 'react-dom';
import { getTarifsForContact, getSetting } from '../services/api';
import {
  REGLES_CLE, lireRegles, raisonMajoration, parametresDepuisGrille, calculerFrais,
} from '../lib/fraisDeplacement';

const ICON_START = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
const ICON_END = ICON_START; // même icône, on différencie via popup

// Helper : ajuste la carte aux bounds de la polyline
function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions && positions.length >= 2) {
      map.fitBounds(positions, { padding: [40, 40] });
    }
  }, [positions, map]);
  return null;
}

// Les erreurs remontées par HERE ou le navigateur sont techniques et en
// anglais. On les traduit ; le reste est repris tel quel plutôt que masqué.
function messageLisible(msg) {
  const m = String(msg || '').toLowerCase();
  if (m.includes('timeout')) return "Le service de cartographie n'a pas répondu à temps.";
  if (m.includes('network') || m.includes('failed to fetch')) return 'Connexion au service de cartographie impossible.';
  if (m.includes('permission') || m.includes('denied')) return "L'accès à votre position a été refusé par le navigateur.";
  if (m.includes('unavailable')) return 'Votre position n’est pas disponible pour le moment.';
  if (m.includes('no route') || m.includes('not found')) return 'Aucun itinéraire trouvé entre ces deux adresses.';
  return msg || 'Erreur inconnue';
}

function formatDistance(m) {
  if (m == null) return '—';
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(1)} km`;
}
function formatDuration(s) {
  if (s == null) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')}`;
  return `${m} min`;
}

// Petit hook debounce
function useDebouncedValue(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/**
 * AutocompleteInput : input contrôlé qui propose des suggestions HERE.
 * onSelect: ({ label, lat, lng }) => void
 */
function AutocompleteInput({ value, onChange, onSelect, placeholder, biasAt, disabled }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const debouncedValue = useDebouncedValue(value, 250);
  const rootRef = useRef(null);
  // Une adresse posée par « utiliser ma position » ou par une sélection ne doit
  // pas rouvrir la liste de suggestions : c'est ce qui menait à écraser une
  // position déjà exacte.
  const typedRef = useRef(false);
  // Position à l'écran de la liste flottante, recalculée depuis le champ.
  const champRef = useRef(null);
  const listeRef = useRef(null);
  const [ancre, setAncre] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      // La liste vit dans un portail : elle n'est plus un descendant du champ,
      // un clic dedans doit malgré tout compter comme un clic « à l'intérieur ».
      const dansChamp = rootRef.current && rootRef.current.contains(e.target);
      const dansListe = listeRef.current && listeRef.current.contains(e.target);
      if (!dansChamp && !dansListe) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function go() {
      if (!typedRef.current || !debouncedValue || debouncedValue.length < 3) { setItems([]); return; }
      setLoading(true);
      try {
        const items = await autocomplete(debouncedValue, { limit: 8, at: biasAt });
        if (!cancelled) { setItems(items); setOpen(true); }
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    go();
    return () => { cancelled = true; };
  }, [debouncedValue, biasAt]);

  async function pick(item) {
    setOpen(false);
    // L'autocomplete HERE ne renvoie pas toujours les coords directement.
    // Si position absente : geocode sur l'id ou le label.
    let lat = item?.position?.lat;
    let lng = item?.position?.lng;
    if (lat == null || lng == null) {
      try {
        const r = await geocode(item.address?.label || item.title);
        if (r[0]) { lat = r[0].position?.lat; lng = r[0].position?.lng; }
      } catch {}
    }
    const label = item.address?.label || item.title || '';
    // Un seul appel : le parent reçoit libellé ET coordonnées ensemble.
    typedRef.current = false;
    onSelect({ label, lat, lng });
  }

  // Mesure du champ tant que la liste est ouverte. Sans le suivi du
  // défilement, la liste resterait figée là où le champ se trouvait.
  useEffect(() => {
    if (!open) { setAncre(null); return undefined; }
    const mesurer = () => {
      const r = champRef.current && champRef.current.getBoundingClientRect();
      if (r) setAncre({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    mesurer();
    window.addEventListener("scroll", mesurer, true);
    window.addEventListener("resize", mesurer);
    return () => {
      window.removeEventListener("scroll", mesurer, true);
      window.removeEventListener("resize", mesurer);
    };
  }, [open, items.length]);

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <div ref={champRef} style={{ position: 'relative' }}>
        <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          type="text"
          value={value}
          onChange={(e) => { typedRef.current = true; onChange(e.target.value); }}
          onFocus={() => items.length && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            width: '100%', padding: '8px 12px 8px 32px',
            border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14,
            outline: 'none', background: disabled ? '#f1f5f9' : 'white',
          }}
        />
        {loading && (
          <Loader2 size={14} color="#94a3b8" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', animation: 'spin 1s linear infinite' }} />
        )}
      </div>
      {open && items.length > 0 && ancre && createPortal(
        <div ref={listeRef} style={{
          // Position fixe : la liste ne dépend plus d'aucun parent, donc ni
          // l'overflow du corps de la modale ni celui de son cadre ne peuvent
          // la rogner. z-index au-dessus de la modale (1000) et de Leaflet.
          position: 'fixed', top: ancre.top, left: ancre.left, width: ancre.width,
          background: 'white', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,.12)', zIndex: 2000,
          maxHeight: 280, overflowY: 'auto',
        }}>
          {items.map((it, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => pick(it)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 12px', border: 'none', background: 'white',
                borderBottom: idx < items.length - 1 ? '1px solid #f1f5f9' : 'none',
                cursor: 'pointer', fontSize: 13, color: '#0f172a',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <div style={{ fontWeight: 600 }}>{it.title || it.address?.label}</div>
              {it.address?.label && it.title !== it.address.label && (
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{it.address.label}</div>
              )}
            </button>
          ))}
        </div>, document.body)}
    </div>
  );
}

// ── Coûts ────────────────────────────────────────────────────────────────
const euros = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
function formatEuros(v) { return v == null ? '—' : euros.format(v); }

// Un prix HERE est soit une valeur, soit une fourchette (on prend le bas).
function montant(p) {
  if (!p) return null;
  if (p.value != null) return Number(p.value);
  if (p.minimum != null) return Number(p.minimum);
  return null;
}

// Péages d'une section : le total HERE s'il est fourni, sinon la somme des
// péages. Un péage peut proposer plusieurs tarifs (selon le moyen de paiement) :
// ce sont des alternatives, on retient le moins cher.
function peagesSection(section) {
  const detail = [];
  for (const t of section.tolls || []) {
    const prix = (t.fares || []).map((f) => montant(f.convertedPrice || f.price)).filter((v) => v != null);
    if (!prix.length) continue;
    detail.push({ nom: t.tollSystem || t.fares?.[0]?.name || 'Péage', prix: Math.min(...prix) });
  }
  const total = montant(section.summary?.tolls?.total);
  return { total: total != null ? total : detail.reduce((s, d) => s + d.prix, 0), detail };
}

// Une réponse HERE = un ou plusieurs itinéraires, chacun découpé en sections
// (une par étape intermédiaire, ou par ferry). On les remet bout à bout.
function lireItineraire(r, { avecPeages }) {
  const sections = r.sections || [];
  let length = 0; let duration = 0; let baseDuration = 0; let peages = 0;
  const positions = []; const actions = []; const detailPeages = []; const avis = [];
  for (const s of sections) {
    length += s.summary?.length || 0;
    duration += s.summary?.duration || 0;
    baseDuration += s.summary?.baseDuration ?? s.summary?.duration ?? 0;
    positions.push(...decodeFlexPolyline(s.polyline));
    actions.push(...(s.actions || []));
    if (avecPeages) {
      const p = peagesSection(s);
      peages += p.total;
      detailPeages.push(...p.detail);
    }
    for (const n of s.notices || []) {
      if (String(n.code || '').startsWith('violated')) avis.push(n.title || n.code);
    }
  }
  return {
    length, duration, baseDuration, positions, actions, avis,
    peages: avecPeages ? peages : null,
    detailPeages,
    depart: sections[0]?.departure?.time || null,
    arrivee: sections[sections.length - 1]?.arrival?.time || null,
  };
}

// HERE renvoie l'heure locale avec son décalage : on l'affiche telle quelle.
function heure(iso) { return iso ? iso.slice(11, 16).replace(':', 'h') : '—'; }
function jourDifferent(a, b) { return a && b && a.slice(0, 10) !== b.slice(0, 10); }

function lienMappy(origin, destination, destinationLabel) {
  // Repli sur les coordonnées quand un libellé manque : un segment vide
  // produisait une URL Mappy inutilisable.
  const pt = (p, fallbackLabel) => (p.lat != null && p.lng != null)
    ? `${p.lat},${p.lng}`
    : (p.label || fallbackLabel || '');
  const de = pt(origin, '');
  const vers = pt(destination, destinationLabel);
  const base = 'https://fr.mappy.com/itineraire#/';
  return de
    ? `${base}de/${encodeURIComponent(de)}/vers/${encodeURIComponent(vers)}`
    : `${base}vers/${encodeURIComponent(vers)}`;
}

const ICON_VIA = new L.DivIcon({
  className: '',
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#f59e0b;border:2px solid white;box-shadow:0 0 0 1px #b45309"></div>',
  iconSize: [14, 14], iconAnchor: [7, 7],
});

const AVOID_OPTIONS = [
  { v: 'tollRoad', l: 'Péages' },
  { v: 'controlledAccessHighway', l: 'Autoroutes' },
  { v: 'ferry', l: 'Ferries' },
];

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 };
const chip = (actif) => ({
  padding: '6px 12px', fontSize: 12, borderRadius: 6,
  border: '1px solid ' + (actif ? '#4f46e5' : '#cbd5e1'),
  background: actif ? '#eef2ff' : 'white',
  color: actif ? '#4f46e5' : '#475569',
  cursor: 'pointer', fontWeight: actif ? 700 : 500,
});
const petitChamp = {
  width: 70, padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12,
};

export default function ItineraireModal({ contact, onClose }) {
  // Adresse arrivée pré-remplie depuis le contact
  const destinationLabel = useMemo(() => {
    const parts = [contact?.adresse, contact?.cp, contact?.ville, contact?.pays_nom].filter(Boolean);
    return parts.join(' ');
  }, [contact]);
  const initialDestCoords = useMemo(() => {
    const lat = parseFloat(contact?.addr_lat);
    const lng = parseFloat(contact?.addr_lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    return null;
  }, [contact]);

  const [origin, setOrigin] = useState({ label: '', lat: null, lng: null });
  const [destination, setDestination] = useState({
    label: destinationLabel,
    lat: initialDestCoords?.lat ?? null,
    lng: initialDestCoords?.lng ?? null,
  });
  // Étapes intermédiaires, dans l'ordre du trajet
  const [vias, setVias] = useState([]);
  const [transportMode, setTransportMode] = useState('car');
  const [routingMode, setRoutingMode] = useState('fast');
  const [avoid, setAvoid] = useState([]);
  const [departNow, setDepartNow] = useState(true);
  const [departureTime, setDepartureTime] = useState('');
  // Frais de déplacement : règles (page Tarifs) + grille de l'antenne
  const [regles, setRegles] = useState(() => lireRegles(null));
  const [grilleInfo, setGrilleInfo] = useState(null); // { libelle, data } ou null
  const [params, setParams] = useState(null); // { forfait, km_inclus, bareme_km, majore }
  const [borne, setBorne] = useState('classik');
  const [peagesDansFrais, setPeagesDansFrais] = useState(true);
  const [allersRetours, setAllersRetours] = useState([
    { libelle: 'Livraison', trajet: 'aller', quand: '', majore: false, raison: null },
    { libelle: 'Reprise', trajet: 'retour', quand: '', majore: false, raison: null },
  ]);
  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState(null); // [{ length, duration, peages, positions, actions, ... }]
  const [selected, setSelected] = useState(0);
  const [peagesIndispo, setPeagesIndispo] = useState(false);
  const [voirDetailPeages, setVoirDetailPeages] = useState(false);
  const [error, setError] = useState(null);
  const [locating, setLocating] = useState(false);

  const enVoiture = transportMode === 'car';

  // Règles + grille effective de l'antenne (personnalisée, grille nommée ou
  // standard). Les montants restent modifiables ici : c'est une base de devis.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getSetting(REGLES_CLE).catch(() => null),
      contact?.id ? getTarifsForContact(contact.id).catch(() => null) : Promise.resolve(null),
    ]).then(([valeurRegles, tarifs]) => {
      if (cancelled) return;
      const r = lireRegles(valeurRegles);
      setRegles(r);
      const data = tarifs?.data || null;
      setGrilleInfo(data ? {
        libelle: tarifs.isCustom ? 'tarif personnalisé' : (tarifs.grille?.libelle || 'tarif standard'),
        data,
      } : null);
      const p = parametresDepuisGrille(data, r);
      setParams(p);
      // Type de borne par défaut : le premier qui a un forfait majoré
      const types = ['spherik', 'classik', 'prestige'];
      const avecMajore = types.find((t) => (+p.majore.aller?.[t] || 0) > 0 || (+p.majore.retour?.[t] || 0) > 0);
      if (avecMajore) setBorne(avecMajore);
    });
    return () => { cancelled = true; };
  }, [contact?.id]);

  function majAllerRetour(i, patch) {
    setAllersRetours((ars) => ars.map((ar, j) => {
      if (j !== i) return ar;
      const n = { ...ar, ...patch };
      // Changer la date recalcule la majoration ; la case reste modifiable ensuite.
      if ('quand' in patch) {
        n.raison = raisonMajoration(patch.quand, regles);
        n.majore = !!n.raison;
      }
      return n;
    }));
  }

  // Si les coords destination ne sont pas dans le contact, on geocode au mount
  useEffect(() => {
    let cancelled = false;
    if (destination.lat == null && destinationLabel) {
      geocode(destinationLabel)
        .then((items) => {
          if (cancelled || !items[0]?.position) return;
          setDestination((d) => ({ ...d, lat: items[0].position.lat, lng: items[0].position.lng }));
        })
        .catch(() => {});
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleUseMyLocation() {
    if (!navigator.geolocation) {
      setError('Géolocalisation non supportée par ce navigateur');
      return;
    }
    setLocating(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        let label = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        try {
          const items = await reverseGeocode(lat, lng);
          if (items[0]?.address?.label) label = items[0].address.label;
        } catch {}
        setOrigin({ label, lat, lng });
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        setError(messageLisible(err.message) || 'Impossible de récupérer la position');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  // Un point saisi sans être choisi dans la liste n'a pas de coordonnées :
  // on le géocode au moment du calcul.
  async function resoudre(p, fallbackLabel, nom) {
    if (p.lat != null && p.lng != null) return p;
    const label = p.label?.trim() || fallbackLabel;
    if (!label) throw new Error(`Adresse ${nom} requise`);
    const items = await geocode(label);
    if (!items[0]?.position) throw new Error(`Adresse ${nom} introuvable`);
    return { label: p.label || label, lat: items[0].position.lat, lng: items[0].position.lng };
  }

  async function handleCompute() {
    setError(null);
    let o; let d; let v;
    try {
      o = await resoudre(origin, '', 'de départ');
      d = await resoudre(destination, destinationLabel, "d'arrivée");
      // Une étape laissée vide est ignorée plutôt que bloquante.
      v = await Promise.all(vias.filter((x) => x.label?.trim() || x.lat != null)
        .map((x, i) => resoudre(x, '', `de l'étape ${i + 1}`)));
    } catch (e) {
      setError(messageLisible(e.message));
      return;
    }
    setOrigin(o); setDestination(d); setVias(v);

    setLoading(true);
    try {
      const res = await routeHere([o.lat, o.lng], [d.lat, d.lng], {
        transportMode,
        via: v.map((x) => [x.lat, x.lng]),
        routingMode,
        // HERE ne propose pas d'alternatives quand le trajet a des étapes.
        alternatives: v.length ? 0 : 2,
        avoid: enVoiture ? avoid : [],
        departureTime: !departNow && departureTime ? departureTime : undefined,
      });
      const avecPeages = enVoiture && !res?.tollsUnavailable;
      const liste = (res?.routes || []).map((r) => lireItineraire(r, { avecPeages }));
      if (!liste.length) throw new Error('Aucun itinéraire trouvé');
      setRoutes(liste);
      setSelected(0);
      setPeagesIndispo(enVoiture && !!res?.tollsUnavailable);
    } catch (e) {
      setRoutes(null);
      const msg = messageLisible(e?.response?.data?.error || e.message);
      setError(`Calcul de l'itinéraire échoué : ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  // Une fois un itinéraire affiché, changer une option le recalcule : c'est
  // ce qu'on attend d'un comparateur, sans avoir à recliquer.
  const premierRendu = useRef(true);
  useEffect(() => {
    if (premierRendu.current) { premierRendu.current = false; return; }
    if (routes && !loading) handleCompute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transportMode, routingMode, avoid, departNow, departureTime]);

  function swap() {
    // On échange libellés ET coordonnées, sinon on repartirait sur un
    // géocodage inutile ; les étapes sont parcourues dans l'autre sens.
    const o = origin;
    setOrigin(destination);
    setDestination(o);
    setVias((vs) => [...vs].reverse());
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose?.();
  }

  const fraisDe = (it) => (enVoiture && it && params
    ? calculerFrais({
      kmAller: it.length / 1000,
      peageAller: it.peages,
      avecPeages: peagesDansFrais,
      allersRetours,
      p: params,
      borne,
      seuilSur: regles.seuil_sur,
    })
    : null);

  // Repères « le plus rapide » / « le moins cher » quand on compare plusieurs trajets
  const badges = useMemo(() => {
    if (!routes || routes.length < 2) return {};
    const b = {};
    const plusRapide = routes.reduce((m, it, i) => (it.duration < routes[m].duration ? i : m), 0);
    b[plusRapide] = ['Le plus rapide'];
    if (enVoiture && params) {
      const totaux = routes.map((it) => fraisDe(it).total);
      const moinsCher = totaux.reduce((m, t, i) => (t < totaux[m] ? i : m), 0);
      b[moinsCher] = [...(b[moinsCher] || []), 'Le moins cher'];
    }
    return b;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes, enVoiture, params, allersRetours, borne, peagesDansFrais, regles]);

  const r = routes?.[selected];

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 14,
          width: 'min(1180px, 100%)', maxHeight: '94vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid #e2e8f0', gap: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              width: 32, height: 32, borderRadius: 8, background: '#eef2ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Route size={16} color="#4f46e5" />
            </span>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                Itinéraire
              </h3>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                Vers {contact?.prenom || ''} {contact?.nom || ''}{destinationLabel ? ` — ${destinationLabel}` : ''}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: '#64748b', padding: 6, borderRadius: 6,
          }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: 18 }}>
          {/* Form */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, marginBottom: 10 }}>
            <div>
              <label style={labelStyle}>
                <MapPin size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Départ
              </label>
              <AutocompleteInput
                value={origin.label}
                onChange={(v) => setOrigin({ label: v, lat: null, lng: null })}
                onSelect={(item) => setOrigin(item)}
                placeholder="Adresse de départ (ex. lieu de l'événement)…"
                biasAt={destination.lat ? `${destination.lat},${destination.lng}` : undefined}
                disabled={locating}
              />
              <button
                type="button"
                onClick={handleUseMyLocation}
                disabled={locating}
                style={{
                  marginTop: 6, padding: '6px 10px', fontSize: 12, color: '#4f46e5',
                  background: 'white', border: '1px solid #c7d2fe', borderRadius: 6,
                  cursor: locating ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {locating ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Crosshair size={12} />}
                Utiliser ma position
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label
                aria-hidden
                style={{ ...labelStyle, visibility: 'hidden' }}
              >
                &nbsp;
              </label>
              <div style={{ display: 'flex', alignItems: 'center', height: 36 }}>
                <button
                  type="button"
                  onClick={swap}
                  title="Intervertir départ et arrivée"
                  style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #cbd5e1', background: 'white', color: '#475569', cursor: 'pointer', fontSize: 15, lineHeight: 1 }}
                >
                  ⇄
                </button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>
                <Navigation size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Arrivée
              </label>
              <AutocompleteInput
                value={destination.label}
                onChange={(v) => setDestination({ label: v, lat: null, lng: null })}
                onSelect={(item) => setDestination(item)}
                placeholder="Adresse d'arrivée…"
              />
              <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
                Pré-rempli depuis la fiche du contact (modifiable).
              </div>
            </div>
          </div>

          {/* Étapes intermédiaires */}
          {vias.map((v, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Étape {i + 1}</label>
                <AutocompleteInput
                  value={v.label}
                  onChange={(val) => setVias((vs) => vs.map((x, j) => (j === i ? { label: val, lat: null, lng: null } : x)))}
                  onSelect={(item) => setVias((vs) => vs.map((x, j) => (j === i ? item : x)))}
                  placeholder="Adresse de l'étape…"
                  biasAt={destination.lat ? `${destination.lat},${destination.lng}` : undefined}
                />
              </div>
              <button
                type="button"
                onClick={() => setVias((vs) => vs.filter((_, j) => j !== i))}
                title="Retirer cette étape"
                style={{ height: 36, width: 36, borderRadius: 8, border: '1px solid #cbd5e1', background: 'white', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {vias.length < 5 && (
            <button
              type="button"
              onClick={() => setVias((vs) => [...vs, { label: '', lat: null, lng: null }])}
              style={{
                marginBottom: 14, padding: '5px 10px', fontSize: 12, color: '#475569',
                background: 'white', border: '1px dashed #cbd5e1', borderRadius: 6,
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <Plus size={12} /> Ajouter une étape
            </button>
          )}

          {/* Options */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '10px 22px', alignItems: 'center',
            padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: 10, marginBottom: 14, fontSize: 12, color: '#475569',
          }}>
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                { v: 'car', l: 'Voiture' },
                { v: 'pedestrian', l: 'À pied' },
                { v: 'bicycle', l: 'Vélo' },
              ].map((m) => (
                <button key={m.v} type="button" onClick={() => setTransportMode(m.v)} style={chip(transportMode === m.v)}>
                  {m.l}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                { v: 'fast', l: 'Le plus rapide' },
                { v: 'short', l: 'Le plus court' },
              ].map((m) => (
                <button key={m.v} type="button" onClick={() => setRoutingMode(m.v)} style={chip(routingMode === m.v)}>
                  {m.l}
                </button>
              ))}
            </div>
            {enVoiture && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 600 }}>Éviter :</span>
                {AVOID_OPTIONS.map((a) => (
                  <label key={a.v} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={avoid.includes(a.v)}
                      onChange={(e) => setAvoid((av) => (e.target.checked ? [...av, a.v] : av.filter((x) => x !== a.v)))}
                    />
                    {a.l}
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Clock size={13} />
              <select
                value={departNow ? 'now' : 'at'}
                onChange={(e) => setDepartNow(e.target.value === 'now')}
                style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, background: 'white' }}
              >
                <option value="now">Partir maintenant</option>
                <option value="at">Partir le…</option>
              </select>
              {!departNow && (
                <input
                  type="datetime-local"
                  value={departureTime}
                  onChange={(e) => setDepartureTime(e.target.value)}
                  style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}
                />
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleCompute}
              disabled={loading}
              style={{
                padding: '8px 16px', fontSize: 13, fontWeight: 600,
                background: loading ? '#94a3b8' : '#4f46e5', color: 'white',
                border: 'none', borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {loading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Route size={14} />}
              Afficher l'itinéraire
            </button>
            <a
              href={lienMappy(origin, destination, destinationLabel)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <ExternalLink size={12} /> Ouvrir dans Mappy
            </a>
          </div>

          {error && (
            <div style={{ padding: 10, background: '#fef2f2', color: '#991b1b', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}

          {/* Comparaison des itinéraires proposés */}
          {routes && routes.length > 1 && (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${routes.length}, minmax(0, 1fr))`, gap: 10, marginBottom: 14 }}>
              {routes.map((it, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelected(i)}
                  style={{
                    textAlign: 'left', padding: 10, borderRadius: 10, cursor: 'pointer',
                    border: '2px solid ' + (i === selected ? '#4f46e5' : '#e2e8f0'),
                    background: i === selected ? '#eef2ff' : 'white',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'baseline' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{formatDuration(it.duration)}</span>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{formatDistance(it.length)}</span>
                  </div>
                  {enVoiture && (
                    <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                      Péages {it.peages == null ? 'n.c.' : formatEuros(it.peages)}
                      {params && <> · Frais {formatEuros(fraisDe(it).total)}</>}
                    </div>
                  )}
                  {(badges[i] || []).map((b) => (
                    <span key={b} style={{
                      display: 'inline-block', marginTop: 6, marginRight: 4, padding: '1px 7px',
                      fontSize: 10, fontWeight: 700, borderRadius: 10, background: '#dcfce7', color: '#166534',
                    }}>{b}</span>
                  ))}
                </button>
              ))}
            </div>
          )}

          {/* Résultats */}
          {r && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: 14 }}>
              <div style={{ height: 480, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <MapContainer
                  style={{ height: '100%', width: '100%' }}
                  center={[origin.lat, origin.lng]}
                  zoom={10}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap'
                  />
                  {/* Les alternatives en gris, cliquables ; la sélection par-dessus */}
                  {routes.map((it, i) => i !== selected && (
                    <Polyline
                      key={i}
                      positions={it.positions}
                      pathOptions={{ color: '#94a3b8', weight: 5, opacity: 0.7 }}
                      eventHandlers={{ click: () => setSelected(i) }}
                    />
                  ))}
                  <Polyline positions={r.positions} pathOptions={{ color: '#4f46e5', weight: 6, opacity: 0.9 }} />
                  <Marker position={[origin.lat, origin.lng]} icon={ICON_START}>
                    <Popup><strong>Départ</strong><br/>{origin.label}</Popup>
                  </Marker>
                  {vias.filter((v) => v.lat != null).map((v, i) => (
                    <Marker key={i} position={[v.lat, v.lng]} icon={ICON_VIA}>
                      <Popup><strong>Étape {i + 1}</strong><br/>{v.label}</Popup>
                    </Marker>
                  ))}
                  <Marker position={[destination.lat, destination.lng]} icon={ICON_END}>
                    <Popup><strong>Arrivée</strong><br/>{destination.label}</Popup>
                  </Marker>
                  <FitBounds positions={r.positions} />
                </MapContainer>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, border: '1px solid #e2e8f0' }}>
                <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span style={{ fontSize: 26, fontWeight: 700, color: '#0f172a' }}>{formatDuration(r.duration)}</span>
                    <span style={{ fontSize: 15, color: '#475569' }}>{formatDistance(r.length)}</span>
                  </div>
                  {r.baseDuration && r.baseDuration !== r.duration && (
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>sans trafic : {formatDuration(r.baseDuration)}</div>
                  )}
                  {r.depart && r.arrivee && (
                    <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
                      Départ {heure(r.depart)} → arrivée {heure(r.arrivee)}
                      {jourDifferent(r.depart, r.arrivee) && ` (le ${r.arrivee.slice(8, 10)}/${r.arrivee.slice(5, 7)})`}
                    </div>
                  )}
                </div>

                {enVoiture && (
                  <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #e2e8f0', fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ color: '#475569' }}>Péages</span>
                      <span style={{ fontWeight: 600, color: '#0f172a' }}>
                        {r.peages == null ? 'Non disponible' : r.peages === 0 ? 'Aucun' : formatEuros(r.peages)}
                      </span>
                    </div>
                    {r.detailPeages.length > 0 && (
                      <div style={{ marginBottom: 4 }}>
                        <button
                          type="button"
                          onClick={() => setVoirDetailPeages((x) => !x)}
                          style={{ background: 'none', border: 'none', padding: 0, color: '#4f46e5', fontSize: 11, cursor: 'pointer' }}
                        >
                          {voirDetailPeages ? 'Masquer le détail' : `Détail (${r.detailPeages.length} péage${r.detailPeages.length > 1 ? 's' : ''})`}
                        </button>
                        {voirDetailPeages && r.detailPeages.map((p, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            <span>{p.nom}</span><span>{formatEuros(p.prix)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>Péages pour un trajet simple, dans ce sens.</div>
                    {peagesIndispo && (
                      <div style={{ marginTop: 6, fontSize: 11, color: '#b45309' }}>
                        Le coût des péages n'a pas pu être obtenu : les frais ne les comprennent pas.
                      </div>
                    )}
                  </div>
                )}

                {r.avis.length > 0 && (
                  <div style={{ marginBottom: 12, padding: 8, background: '#fffbeb', color: '#92400e', borderRadius: 6, fontSize: 11 }}>
                    Toutes les restrictions demandées n'ont pas pu être respectées sur ce trajet.
                  </div>
                )}

                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
                  Étapes ({r.actions.length})
                </div>
                <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {r.actions.map((a, i) => (
                    <div key={i} style={{
                      display: 'flex', gap: 8, padding: 8, background: 'white',
                      borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12,
                    }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: '50%', background: '#eef2ff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#4f46e5', fontWeight: 700, fontSize: 11, flexShrink: 0,
                      }}>{i + 1}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#0f172a' }}>{a.instruction}</div>
                        {(a.length != null || a.duration != null) && (
                          <div style={{ marginTop: 2, color: '#94a3b8', fontSize: 11 }}>
                            {a.length != null && formatDistance(a.length)}
                            {a.length != null && a.duration != null && ' · '}
                            {a.duration != null && formatDuration(a.duration)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {r && enVoiture && params && (() => {
            const frais = fraisDe(r);
            const aDesMajores = ['aller', 'retour'].some((t) => Object.values(params.majore[t] || {}).some((v) => +v > 0));
            const setP = (k, v) => setParams((p) => ({ ...p, [k]: Math.max(0, parseFloat(v) || 0) }));
            return (
              <div style={{ marginTop: 14, padding: 14, border: '1px solid #c7d2fe', background: '#f8faff', borderRadius: 10, fontSize: 13, color: '#475569' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Frais de déplacement</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {grilleInfo ? `D'après la grille de l'antenne (${grilleInfo.libelle})` : 'Grille de l\'antenne introuvable : saisissez les montants'}
                      {' · '}{formatDistance(r.length)} l'aller simple
                    </div>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#4f46e5' }}>{formatEuros(frais.total)}</div>
                </div>

                {/* Paramètres pré-remplis, modifiables pour ce devis */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', alignItems: 'center', paddingBottom: 10, marginBottom: 10, borderBottom: '1px solid #e2e8f0', fontSize: 12 }}>
                  <span>Forfait <input type="number" min="0" step="1" value={params.forfait} onChange={(e) => setP('forfait', e.target.value)} style={petitChamp} /> €</span>
                  <span>jusqu'à <input type="number" min="0" step="1" value={params.km_inclus} onChange={(e) => setP('km_inclus', e.target.value)} style={petitChamp} /> km
                    <span style={{ color: '#94a3b8' }}> ({regles.seuil_sur === 'aller' ? 'aller simple' : 'aller-retour'})</span></span>
                  <span>puis <input type="number" min="0" step="0.01" value={params.bareme_km} onChange={(e) => setP('bareme_km', e.target.value)} style={petitChamp} /> €/km</span>
                  {aDesMajores && (
                    <span>Borne&nbsp;
                      <select value={borne} onChange={(e) => setBorne(e.target.value)} style={{ ...petitChamp, width: 'auto' }}>
                        <option value="spherik">Spherik</option>
                        <option value="classik">Classik</option>
                        <option value="prestige">Prestige</option>
                      </select>
                      <span style={{ color: '#94a3b8' }}> (forfait majoré)</span>
                    </span>
                  )}
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <input type="checkbox" checked={peagesDansFrais} onChange={(e) => setPeagesDansFrais(e.target.checked)} />
                    Inclure les péages (× 2 par aller-retour)
                  </label>
                </div>

                {/* Un aller-retour par ligne */}
                {frais.lignes.map((l, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '150px 190px minmax(0,1fr) 90px 28px', gap: 10, alignItems: 'center', padding: '6px 0', borderBottom: '1px dashed #e2e8f0' }}>
                    <input
                      value={l.libelle}
                      onChange={(e) => majAllerRetour(i, { libelle: e.target.value })}
                      style={{ ...petitChamp, width: '100%', fontWeight: 600 }}
                    />
                    <input
                      type="datetime-local"
                      value={l.quand}
                      onChange={(e) => majAllerRetour(i, { quand: e.target.value })}
                      title="Date et heure de départ : sert à détecter nuit, dimanche et jours fériés"
                      style={{ ...petitChamp, width: '100%' }}
                    />
                    <div style={{ fontSize: 12 }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={l.majore} onChange={(e) => majAllerRetour(i, { majore: e.target.checked })} />
                        Majoré
                      </label>
                      {l.raison && <span style={{ color: '#92400e' }}> — {l.raison}</span>}
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {l.majoreApplique
                          ? `Forfait ${l.trajet === 'aller' ? 'livraison aller' : 'livraison retour'} majoré`
                          : `Forfait ${formatEuros(params.forfait / 2)} (moitié de l'aller-retour à ${formatEuros(params.forfait)})${l.kmFactures > 0 ? ` + ${Math.round(l.kmFactures)} km × ${formatEuros(params.bareme_km)}` : ''}`}
                        {l.majoreSansForfait && ' (pas de forfait majoré dans la grille : tarif normal)'}
                        {l.peages > 0 && ` + péages ${formatEuros(l.peages)}`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{formatEuros(l.total)}</div>
                    <button
                      type="button"
                      onClick={() => setAllersRetours((ars) => ars.filter((_, j) => j !== i))}
                      title="Retirer cet aller-retour"
                      style={{ border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', padding: 0 }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setAllersRetours((ars) => [...ars, {
                    libelle: `Aller-retour ${ars.length + 1}`,
                    trajet: ars.length % 2 === 0 ? 'aller' : 'retour',
                    quand: '', majore: false, raison: null,
                  }])}
                  style={{
                    marginTop: 8, padding: '5px 10px', fontSize: 12, color: '#475569',
                    background: 'white', border: '1px dashed #cbd5e1', borderRadius: 6,
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <Plus size={12} /> Ajouter un aller-retour
                </button>
              </div>
            );
          })()}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
